import React from 'react';
import {
  Autocomplete,
  TextField,
  SxProps,
} from "@mui/material";

interface SingleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  helperText?: string;
  options?: string[];
  sx?: SxProps;
  normalizeValue?: (value: string) => string;
  inputValue?: string;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  error?: boolean;
  disabled?: boolean;
  customHeader?: React.ReactNode;
}

const SingleAutocomplete: React.FC<SingleAutocompleteProps> = ({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  options = [],
  sx,
  normalizeValue,
  inputValue,
  onInputChange,
  error = false,
  disabled = false,
  customHeader,
}) => {
  return (
    <Autocomplete
      freeSolo
      value={value}
      onChange={(_, newValue) => {
        const processedValue = typeof newValue === 'string' 
          ? newValue 
          : (newValue as any)?.inputValue || '';
        const finalValue = normalizeValue ? normalizeValue(processedValue) : processedValue;
        onChange(finalValue);
      }}
      options={options}
      getOptionLabel={(option) => {
        if (!option) return '';
        if (typeof option === 'string') return option;
        return (option as any).inputValue || '';
      }}
      inputValue={inputValue}
      onInputChange={onInputChange}
      disabled={disabled}
      sx={sx}
      ListboxProps={{
        sx: customHeader ? { p: 0 } : undefined,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          variant="outlined"
          fullWidth
          error={error}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            {option}
          </li>
        );
      }}
      ListboxComponent={customHeader ? React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
        ({ children, ...other }, ref) => (
          <ul {...other} ref={ref}>
            {customHeader}
            {children}
          </ul>
        )
      ) : undefined}
    />
  );
};

export default SingleAutocomplete;